import { ListenerTypes, isSafari, listener, store } from "../shared";

const enum DocumentHiddenType {
  hidden = "hidden",
  moz = "moz",
  ms = "ms",
  o = "o",
  webkit = "webkit",
}

interface PageVisibilityStorage {
  pageHiddenMonitors: any[];
  hiddenVendorPrefix: DocumentHiddenType | null;
}

export const PageVisibility = (() => {
  const storage: PageVisibilityStorage = {
    pageHiddenMonitors: [],
    hiddenVendorPrefix: null,
  };

  const getHiddenVendorPrefix = () => {
    if (storage.hiddenVendorPrefix) return storage.hiddenVendorPrefix;

    let hiddenVendorPrefix: DocumentHiddenType | null = null;

    const DocumentHiddenPropertyVendorPrefixes = [
      DocumentHiddenType.moz,
      DocumentHiddenType.ms,
      DocumentHiddenType.o,
      DocumentHiddenType.webkit,
    ];

    if ("hidden" in document) {
      hiddenVendorPrefix = DocumentHiddenType.hidden;
    } else {
      /**
       * https://web.dev/pagevisibility-intro/#document-visibility-properties
       * `document.hidden` api is only vendor-prefixed in Android Browser,
       * which needs to use prefixed versions defined in `DocumentHiddenPropertyVendorPrefixes` constant.
       * All other supporting browsers (IE 10+, Firefox, Chrome, Safari) implement the un-prefixed version.
       */
      hiddenVendorPrefix =
        DocumentHiddenPropertyVendorPrefixes.find((vendorPrefix) => {
          return `${vendorPrefix}Hidden` in document;
        }) || null;
    }

    storage.hiddenVendorPrefix = hiddenVendorPrefix;
    return hiddenVendorPrefix;
  };

  const getIsPageHidden = () => {
    const hiddenVendorPrefix = getHiddenVendorPrefix();

    if (document["visibilityState"] === "hidden") return true;
    if (document["visibilityState"] === "visible") return false;

    if (hiddenVendorPrefix) {
      // Fallback to document[enum DocumentHiddenType] if visibilityState is not hidden or visible value
      return document[hiddenVendorPrefix] === "hidden";
    }
    // Fallback to document.hidden is visibilityState is not hidden or visible value
    return document.hidden;
  };

  const onPageVisibilityChangeHandler = () => {
    const isPageHidden = getIsPageHidden();

    if (isPageHidden !== undefined) {
      store.set("isPageHidden", isPageHidden);
      listener.emit(ListenerTypes.visibilityChange, isPageHidden);
    }
  };

  const setListener = () => {
    window.addEventListener(
      "visibilitychange",
      onPageVisibilityChangeHandler,
      true
    );

    /**
     * Safari does not reliably call `pagehide` or `visibilitychange` events,
     * so using `beforeunload` as a backup.
     *
     *
     * Safari bugs(marked as resolved but some reports of the bug are
     * still hapening in 2022)
     *  - https://bugs.webkit.org/show_bug.cgi?id=151610
     *  - https://bugs.webkit.org/show_bug.cgi?id=151234
     *
     * This should only be applied in Safari because Firefox would prevent the page from
     * being eligible for bfcache.
     *
     *
     * `beforeunload` Event bfcache Firefox Warning:
     * https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
     *
     */

    if (isSafari) {
      window.addEventListener(
        "beforeunload",
        onPageVisibilityChangeHandler,
        true
      );
    }

    listener.set(ListenerTypes.visibilityChange);
  };

  const setLegacyPageVisibilityListener = () => {};

  return {
    getIsPageHidden,
    start: () => {
      const hiddenVendorPrefix = getHiddenVendorPrefix();

      if (document.visibilityState || hiddenVendorPrefix) {
        setListener();
      }
    },
  };
})();
